<template>
  <div>
    <PageHeader v-if="!embedded" :items="items" />
    <div class="mycases create-case">
      <div class="pageheader">
        <h2>{{ $t("cases") }}: {{ totalRecords }}</h2>
        <div class="pageheader-right">
          <div class="d-flex align-items-center gap-1 filter-by-color">
            <!-- <b-form-group class="mb-0" label="" label-for="status-filter">
              <select class="form-control" v-model="form.status">
                <option
                  v-for="option in filterOptions"
                  :value="option.value"
                  :key="option.value"
                >
                  {{ option.text }}
                </option>
              </select>
            </b-form-group> -->
            <div class="d-flex align-items-center gap-1 filter-by-color">
              <p>{{ $t("status") }}</p>
              <b-form-group class="mb-0">
                <MultiSelect
                  v-model="form.caseStatus"
                  :options="caseStatusOptions"
                  :custom-label="(option) => option.text"
                  track-by="value"
                  :multiple="true"
                  :placeholder="$t('selectOption')"
                />
              </b-form-group>
            </div>
          </div>

          <div class="search">
            <div class="icon">
              <ElementIcon v-bind:icon="'searchIcon'" />
            </div>
            <input
              type="text"
              v-model="form.search"
              :placeholder="$t('searchFor')"
            />
          </div>
          <div class="divider"></div>
          <div class="d-flex align-items-center gap-1 filter-by-color">
            <!-- <p>{{ $t("createdDate") }}</p> -->
            <button
              type="button"
              class="btn btn-outline-secondary created-filter-toggle"
              @click="openCreatedFilterModal"
            >
              {{ $t("filters") }}
            </button>
            
          </div>
          <div class="d-flex align-items-center gap-1 filter-by-color">
            <p>{{ $t("atLeast") }}</p>
            <StarRating
              :value="filterRatingValue"
              @setRatingValue="setFilterRatingValue"
            />
          </div>
          <button @click="resetRatingFilter" class="btn btn-primary">
            {{ $t("reset") }}
          </button>
        </div>
      </div>

      <div class="dextop-table-view">
        <div class="table-responsive api-keys-table case-list-table">
          <vue-good-table
            styleClass="vgt-table striped"
            :columns="columns"
            :fixed-header="false"
            :rows="rows"
            :pagination-options="{
              enabled: false,
            }"
            mode="remote"
            @on-page-change="onPageChange"
            @on-sort-change="onSortChange"
          >
            <div slot="emptystate" style="text-align: center">
              {{ $t("noDataForTable") }}
            </div>
            <template slot="table-row" slot-scope="props">
              <ActionDropdown
                v-if="props.column.field === 'action' && props.row"
                :row="props.row"
                @handlePreviewClick="handlePreviewClick"
                @handlePreviewResultClick="handlePreviewResultClick"
                @loadItems="loadItems"
              />
              <span v-else-if="props.column.field === 'name'">
                <p class="ellipsis-text" :title="props.row.name">
                  {{ props.row.name }}
                </p>
              </span>
              <span v-else-if="props.column.field === 'productName'">
                <div
                  class="product-tag"
                  :class="
                    translatedProductName(props.row.productName) === 'DentaTEMP'
                      ? 'blue-tag'
                      : translatedProductName(props.row.productName) === 'DentaCAD'
                        ? 'gray-tag'
                        : translatedProductName(props.row.productName) === 'DentaWAX'
                          ? 'pink-tag'
                          : translatedProductName(props.row.productName) === 'DentaBRIDGE' ||
                          translatedProductName(props.row.productName) === 'DentaBridge' ||
                          translatedProductName(props.row.productName) === 'dentaBridge'
                            ? 'dark-pink-tag'
                            : 'undefined-tag'
                  "
                >
                  {{ translatedProductName(props.row.productName) }}
                </div>
              </span>
              <span
                class="d-flex align-items-center gap-2"
                v-else-if="props.column.field === 'status'"
              >
                <span
                  v-if="props.row.outcome === 'rejected'"
                  :title="'rejected'"
                  class="status-chip"
                  :data-status="props.row.outcome"
                >
                  {{ $t("rejected") }}
                </span>
                <span
                  v-else
                  class="status-chip"
                  :data-status="getDisplayStatus(props.row)"
                  :title="getStatusTitle(getDisplayStatus(props.row))"
                  v-b-tooltip.hover
                >
                  {{ getStatusTitle(getDisplayStatus(props.row)) }}
                </span>
              </span>
              <span v-else-if="props.column.field === 'Company.companyName'">
                <p class="ellipsis-text" :title="props.row.companyName">
                  {{ props.row.companyName }}
                </p>
              </span>
              <span v-else-if="props.column.field === 'updatedAt'">
                <span
                  class="created-chip"
                  v-b-tooltip.hover
                  :title="getUpdatedHoverText(props.row)"
                >
                  {{ $createdBucket(props.row.updatedAt) }}
                </span>
              </span>
              <span v-else-if="props.column.field === 'createdAt'">
                <span
                  class="created-chip"
                  v-b-tooltip.hover
                  :title="$dateFormatter(props.row.createdAt, $i18n.locale)"
                >
                  {{ $createdBucket(props.row.createdAt) }}
                </span>
              </span>
              <span v-else-if="props.column.field === 'assignee'">
                <span>
                  {{ props.row.assignee ? (userProfiles[props.row.assignee].first_name || "") +
                      " " +
                      (userProfiles[props.row.assignee].last_name || "") : $t('noOne') }}
                </span>
                <feather-icon class="cursor-pointer" size="16" icon="Edit2Icon" @click="openAssigneeModal(props.row)" />
              </span>
              <!-- Column: Common -->
              <span v-else>
                {{ props.formattedRow[props.column.field] }}
              </span>
            </template>

            <!-- pagination -->
          </vue-good-table>
        </div>
      </div>
      <div class="d-flex align-items-center justify-content-between mb-5">
        <div class="d-flex align-items-center gap-1 filter-by-color">
          <p>{{ $t("perPage") }}</p>
          <b-form-group class="m-0">
            <select class="form-control" v-model="form.perPage">
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="500">500</option>
            </select>
          </b-form-group>
        </div>
        <div>
          <b-pagination
            v-model="currentPage"
            :total-rows="totalRecords"
            :per-page="form.perPage"
            @input="(value) => onPageChange({ currentPage: value })"
            aria-controls="case-list"
          ></b-pagination>
        </div>
      </div>
      <div class="reponsive-view-cards">
        <b-row>
          <!-- <b-col cols="12">
            <span
              class="d-flex align-items-center position-relative zindex-4 gap-1 mb-1"
            >
              <SelectAllCasesCheckbox @toggleSelectAll="toggleSelectAll" />
              <strong>{{ $t("selectAllCases") }}</strong>
            </span>
          </b-col> -->
          <b-col
            cols="12"
            lg="4"
            md="6"
            v-for="(row, index) in rows"
            :key="index"
          >
            <div class="card">
              <div
                class="card-header d-flex align-items-center justify-content-between"
              >
                <span class="d-flex align-items-center gap-1">
                  <!-- <div class="checkbox-s-group">
                    <input
                      type="checkbox"
                      class="checkbox-input"
                      :id="'case-' + row.id"
                      v-model="selectedCases"
                      :value="row.id"
                    />
                    <label
                      :for="'case-' + row.id"
                      class="checkbox-label m-0"
                    ></label>
                  </div> -->
                  <h4 class="m-0">{{ row.name }}</h4>
                </span>
                <span v-if="row">
                  <ActionDropdown
                    :row="row"
                    @handlePreviewClick="handlePreviewClick"
                    @handlePreviewResultClick="handlePreviewResultClick"
                    @loadItems="loadItems"
                  />
                </span>
              </div>
              <div class="card-body pt-0">
                <ul class="card-list">
                  <li v-if="!embedded">
                    <div class="left">
                      <strong>{{ $t("caseId") }}:</strong>
                    </div>
                    <div class="right">{{ row.id }}</div>
                  </li>
                  <li v-if="!embedded">
                    <div class="left">
                      <strong>{{ $t("company") }}:</strong>
                    </div>
                    <div class="right">{{ row.companyName }}</div>
                  </li>
                  <li class="d-flex">
                    <div class="left">
                      <strong>{{ $t("status") }}:</strong>
                    </div>
                    <div class="right">
                      <span class="d-flex align-items-center gap-2">
                        
                        <span
                          v-if="row.outcome === 'rejected'"
                          :data-status="row.outcome"
                          class="status-chip"
                        >
                        {{ getStatusTitle(getDisplayStatus(row)) }}
                        </span>
                        <span
                          v-else
                          class="status-chip"
                          :data-status="getDisplayStatus(row)"
                          :title="getStatusTitle(getDisplayStatus(row))"
                          v-b-tooltip.hover
                        >
                          {{ getStatusTitle(getDisplayStatus(row)) }}
                        </span>
                      </span>
                    </div>
                  </li>
                  
                  <li class="d-flex">
                    <div class="left">
                      <strong>{{ $t("created") }}:</strong>
                    </div>
                    <div class="right">
                      <span>
                        {{ $createdBucket(row.createdAt) }}
                      </span>
                    </div>
                  </li>
                  <li class="d-flex">
                    <div class="left">
                      <strong>{{ $t("updated") }}:</strong>
                    </div>
                    <div class="right">
                      <span>
                        {{ $createdBucket(row.updatedAt) }}
                      </span>
                    </div>
                  </li>
                  <li class="d-flex">
                    <div class="left">
                      <strong>{{ $t("assignee") }}:</strong>
                    </div>
                    <div class="right d-flex align-items-center gap-1">
                      <span>
                        {{
                          row.assignee
                            ? (userProfiles[row.assignee].first_name || "") +
                              " " +
                              (userProfiles[row.assignee].last_name || "")
                            : $t("noOne")
                        }}
                      </span>
                      <feather-icon
                        class="cursor-pointer"
                        size="16"
                        icon="Edit2Icon"
                        @click="openAssigneeModal(row)"
                      />
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </b-col>
          <b-col cols="12">
            <div class="d-flex justify-content-center mt-2">
              <b-pagination
                :value="1"
                :total-rows="totalRecords"
                :per-page="form.perPage"
                first-number
                last-number
                align="right"
                prev-class="prev-item"
                next-class="next-item"
                class="mt-1 mb-0"
                @input="(value) => onPageChange({ currentPage: value })"
              >
                <template #prev-text>
                  <feather-icon icon="ChevronLeftIcon" size="18" />
                </template>
                <template #next-text>
                  <feather-icon icon="ChevronRightIcon" size="18" />
                </template>
              </b-pagination>
            </div>
          </b-col>
        </b-row>
      </div>
      <!--==================================-->


      <!-- <EditFlagModal
        v-if="showModal"
        :tag="tag"
        :selectedId="selectedId"
        :existingFlags="existingFlags"
        :showModal="showModal"
        @loadFlags="loadFlags"
        @loadItems="loadItems"
        @closeModal="showModal = false"
      /> -->
      <CaseFilterModal
        :visible="showCreatedFilterModal"
        :from="createdDateDraft.from"
        :to="createdDateDraft.to"
        :selected-filter="createdDateDraft.selectedFilter"
        :company-ids="createdDateDraft.companyIds"
        :product-types="createdDateDraft.productTypes"
        :existing-flags="existingFlags"
        :customers="customers"
        :needs-manual-approval-filter="createdDateDraft.needsManualApprovalFilter"
        :margin-line-filter="createdDateDraft.marginLineFilter"
        @apply="applyCreatedFilter"
        @clear="clearCreatedFilter"
        @close="showCreatedFilterModal = false"
      />
      <!---===================================-->
      <div class="c-modal" v-show="showPreviewModal">
        <div class="c-modal-content">
          <div class="c-modal-header">
            <h3>{{ $t("preview") }}</h3>
            <div class="x-icon" @click="closePreviewModal">
              <feather-icon size="16" icon="XIcon" />
            </div>
          </div>
          <div class="c-modal-body">
            <PreviewModel
              v-if="showPreviewModal"
              :upper="currentUpper"
              :lower="currentLower"
              :upperResult="resultUpper"
              :lowerResult="resultLower"
            />
          </div>
        </div>
      </div>
      <!---===================================-->
      <!---===================================-->
      <div class="c-modal" v-show="showPreviewResultModal">
        <div class="c-modal-content">
          <div class="c-modal-header">
            <h3>{{ $t("preview") }}</h3>
            <div class="x-icon" @click="closePreviewResultModal">
              <feather-icon size="16" icon="XIcon" />
            </div>
          </div>
          <div class="c-modal-body">
            <PreviewResultModel
              v-if="showPreviewResultModal"
              :results="results"
            />
          </div>
        </div>
      </div>
      <AssigneeModal
        :visible="showAssigneeModal"
        @close="closeAssigneeModal"
        @loadItems="loadItems"
        :caseId="selectedAssigneeId"
        :assignedAssignee="assignedAssignee"
      />
      <!---===================================-->
    </div>
  </div>
</template>

<script>
import CookieButton from "@/components/elements/CookieButton.vue";
import ElementIcon from "@/components/elements/Icon.vue";
import { BLink, BRow, BCol, BImg, BPagination, BDropdown } from "bootstrap-vue";
import { mapGetters } from "vuex";
import MultiSelect from "vue-multiselect";
import PreviewModel from "@/components/PreviewModel.vue";
import PreviewResultModel from "@/components/PreviewResultModel.vue";
import PageHeader from "@/components/PageHeader.vue";
import { required, email } from "@validations";
import usersStoreMixin from "@/mixins/usersStoreMixin";
import productTranslator from "@/mixins/productTranslator";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import StarRating from "@/components/elements/StarRating.vue";
import { debounce } from "@/utils/debounce";
import ActionDropdown from "./components/ActionDropdown.vue";
// import EditFlagModal from "./components/EditFlagModal.vue";
import CaseFilterModal from "./components/CaseFilterModal.vue";
import AssigneeModal from "./components/AssigneeModal.vue";
export default {
  mixins: [usersStoreMixin, productTranslator],
  props: {
    companyId: {
      type: String,
      default: null,
    },
    embedded: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    ElementIcon,
    BRow,
    BCol,
    BLink,
    BImg,
    BPagination,
    BDropdown,
    MultiSelect,
    CookieButton,
    PreviewModel,
    PreviewResultModel,
    PageHeader,
    ValidationProvider,
    ValidationObserver,
    StarRating,
    ActionDropdown,
    // EditFlagModal,
    CaseFilterModal,
    AssigneeModal,
  },
  data() {
    return {
      showModal: false,
      showPreviewModal: false,
      showPreviewResultModal: false,
      currentTag: {
        index: null,
        text: "",
        color: "#ffffff",
      },
      intervalId: null,
      previousItems: null,
      currentPage: 1,
      rows: [],
      selectedId: null,
      totalRecords: 0,
      // tag: {
      //   flag: null,
      //   tagColor: "",
      // },
      showAssigneeModal: false,
      selectedAssigneeId: null,
      assignedAssignee: null,

      filterRatingValue: null,

      sortBy: "createdAt",
      sortOrder: "desc",
      form: {
        search: "",
        status: "all",
        selectedFilter: "all",
        perPage: 25,
        caseStatus: [],
        createdFrom: null,
        createdTo: null,
        companyIds: [],
        productTypes: [],
        needsManualApprovalFilter: "all",
        marginLineFilter: "all",
      },
      filteredCases: [],
      currentTag: {
        color: "",
      },
      costsPerUpperJaw: "",
      costsPerLowerJaw: "",
      recalculationFactor: "",
      // existingFlags: [],
      currentUpper: null,
      currentLower: null,
      resultUpper: null,
      resultLower: null,
      results: null,
      isAutoRefresh: false,
      createdDateDraft: {
        from: null,
        to: null,
        selectedFilter: "all",
        companyIds: [],
        productTypes: [],
        needsManualApprovalFilter: "all",
        marginLineFilter: "all",
      },
      showCreatedFilterModal: false,
      statusConfig: {
        created: {
          type: 'pill', bg: '#cfe1fd', color: '#ffffff',
          labels: { en: 'Created', de: 'Erstellt' },
        },
        processing: {
          type: 'pill', bg: '#8b9bf3', color: '#ffffff',
          labels: { en: 'Processing', de: 'In Verarbeitung' },
        },
        reviewing: {
          type: 'pill', bg: '#e9d8a6', color: '#ffffff',
          labels: { en: 'Reviewing', de: 'In Prüfung' },
        },
        adjusting: {
          type: 'pill', bg: '#b8e9a6', color: '#ffffff',
          labels: { en: 'Adjusting', de: 'In Anpassung' },
        },
        queued: {
          type: 'dot',
          bg: '#FACC15',
          color: '#111827',
          labels: { en: 'In Queue', de: 'In Warteschlange' },
        },
        'in-progress': {
          type: 'pill', bg: '#3B82F6', color: '#ffffff',
          labels: { en: 'Processing', de: 'In Verarbeitung' },
        },
        failed: {
          type: 'pill', bg: '#EF4444', color: '#ffffff',
          labels: { en: 'Failed', de: 'Fehler' },
        },
        unsupported: {
          type: 'pill', bg: '#FB923C', color: '#111827',
          labels: { en: 'Not Supported', de: 'Nicht unterstützt' },
        },
        'in-approval': {
          type: 'pill', bg: '#2563EB', color: '#ffffff',
          labels: { en: 'For review', de: 'Zur Prüfung' },
        },
        'review-needed': {
          type: 'pill', bg: '#06EAE9', color: '#0a1724',
          labels: { en: 'For review', de: 'Zur Prüfung' },
        },
        'in-review': {
          type: 'pill', bg: '#2563EB', color: '#ffffff',
          labels: { en: 'In Review', de: 'In Bearbeitung' },
        },
        approved: {
          type: 'pill', bg: '#16A34A', color: '#ffffff',
          labels: { en: 'Approved', de: 'Freigegeben' },
        },
        done: {
          type: 'pill', bg: '#d3f1a7', color: '#ffffff',
          labels: { en: 'Done', de: 'Erledigt' },
        },
        rejected: {
          type: 'pill', bg: '#f87171', color: '#ffffff',
          labels: { en: 'Rejected', de: 'Abgelehnt' },
        },
        paid: {
          type: 'dot', bg: '#6B7280', color: '#ffffff',
          labels: { en: 'Paid', de: 'Bezahlt' },
        },
        downloaded: {
          type: 'dot', bg: '#6B7280', color: '#ffffff',
          labels: { en: 'Downloaded', de: 'Heruntergeladen' },
        },
        archived: {
          type: 'dot', bg: '#D1D5DB', color: '#111827',
          labels: { en: 'Archived', de: 'Archiviert' },
        },
        hidden: {
          type: 'dot', bg: '#374151', color: '#ffffff',
          labels: { en: 'Hidden', de: 'Versteckt' },
        },
      },
    };
  },
  computed: {
    ...mapGetters("users", ["userProfiles"]),
    ...mapGetters("customers", ["customers"]),
    ...mapGetters(["showLoader"]),
    items() {
      return [
        {
          text: this.$t("dentalTwin"),
          to: "/home",
        },
        {
          text: this.$t("cases"),
          active: true,
        },
      ];
    },
    columns() {
      const baseColumns = [
        {
          label: this.$t("caseName"),
          field: "name",
        },
      ];
      
      if (!this.embedded) {
        baseColumns.push({
          label: this.$t("caseId"),
          field: "id",
        });
        baseColumns.push({
          label: this.$t("company"),
          field: "Company.companyName",
        });
      }
      
      return [
        ...baseColumns,
        {
          label: this.$t("productType"),
          field: "productName",
        },
        {
          label: this.$t("progress"),
          field: "status",
        },
        {
          label: this.$t("created"),
          field: "createdAt",
        },
        {
          label: this.$t("updated"),
          field: "updatedAt",
        },
        {
          label: this.$t("assignee"),
          field: "assignee",
        },
        {
          tdClass: "vgt-center-align",
          thClass: "vgt-center-align",
          label: this.$t("action"),
          field: "action",
          sortable: false,
        },
      ];
    },
    // filterOptions() {
    //   return [
    //     { value: "all", text: this.$t("all") },
    //     { value: "active", text: this.$t("active") },
    //     { value: "finished", text: this.$t("finished") },
    //     { value: "flagged", text: this.$t("flagged") },
    //     { value: "trash", text: this.$t("trash") },
    //   ];
    // },
    caseStatusOptions() {
      return [
        { value: "created", text: this.$t("created") },
        // { value: "failed", text: this.$t("failed") },
        // { value: "hidden", text: this.$t("hidden") },
        // { value: "in-progress", text: this.$t("inProgress") },
        { value: "processing", text: this.$t("processing") },
        { value: "reviewing", text: this.$t("reviewing") },
        { value: "adjusting", text: this.$t("adjusting") },
        { value: "done", text: this.$t("done") },
        { value: "paid", text: this.$t("paid") },
        // { value: "in-approval", text: this.$t("inApproval") },
        // { value: "queued", text: this.$t("queued") },
        // { value: "review-needed", text: this.$t("reviewNeeded") },
        // { value: "archived", text: this.$t("archived") },
        // { value: "downloaded", text: this.$t("downloaded") },
        // { value: "unsupported", text: this.$t("unSupported") },
      ];
    },
  },
  created() {
    this.debouncedFetch = debounce(async (newValue, oldValue) => {
      try {
        await this.loadItems();
      } catch (e) {
        console.error(e);
      }
    }, 300);
  },
  methods: {
    formatFilterDate(value) {
      if (!value) {
        return "";
      }
      return this.$dateFormatter(value, this.$i18n.locale);
    },
    openCreatedFilterModal() {
      this.createdDateDraft = {
        from: this.form.createdFrom,
        to: this.form.createdTo,
        selectedFilter: this.form.selectedFilter,
        companyIds: Array.isArray(this.form.companyIds) ? this.form.companyIds.slice() : [],
        productTypes: Array.isArray(this.form.productTypes) ? this.form.productTypes.slice() : [],
        needsManualApprovalFilter: this.form.needsManualApprovalFilter || "all",
        marginLineFilter: this.form.marginLineFilter || "all",
      };
      this.showCreatedFilterModal = true;
    },
    applyCreatedFilter(payload) {
      let from = payload?.from || null;
      let to = payload?.to || null;
      const selectedFilter = payload?.selectedFilter ?? this.form.selectedFilter;
      const companyIds = Array.isArray(payload?.companyIds)
        ? payload.companyIds.slice()
        : Array.isArray(this.form.companyIds) ? this.form.companyIds.slice() : [];
      const productTypes = Array.isArray(payload?.productTypes)
        ? payload.productTypes.slice()
        : Array.isArray(this.form.productTypes) ? this.form.productTypes.slice() : [];
      const needsManualApprovalFilter = payload?.needsManualApprovalFilter ?? this.form.needsManualApprovalFilter ?? "all";
      const marginLineFilter = payload?.marginLineFilter ?? this.form.marginLineFilter ?? "all";
      if (from && to && new Date(from) > new Date(to)) {
        [from, to] = [to, from];
      }
      this.form.createdFrom = from || null;
      this.form.createdTo = to || null;
      this.form.selectedFilter = selectedFilter;
      this.form.companyIds = companyIds;
      this.form.productTypes = productTypes;
      this.form.needsManualApprovalFilter = needsManualApprovalFilter;
      this.form.marginLineFilter = marginLineFilter;
      this.showCreatedFilterModal = false;
    },
    clearCreatedFilter() {
      this.createdDateDraft = {
        from: null,
        to: null,
        selectedFilter: "all",
        companyIds: [],
        productTypes: [],
        needsManualApprovalFilter: "all",
        marginLineFilter: "all",
      };
      this.form.createdFrom = null;
      this.form.createdTo = null;
      this.form.selectedFilter = "all";
      this.form.companyIds = [];
      this.form.productTypes = [];
      this.form.needsManualApprovalFilter = "all";
      this.form.marginLineFilter = "all";
      this.showCreatedFilterModal = false;
    },
    getStatusMeta(status) {
      const def = { type: 'pill', bg: '#6B7280', color: '#ffffff', i18nKey: status };
      return this.statusConfig[status] || def;
    },
    getDisplayStatus(row) {
      if (!row) return "";
      if (row.outcome === "rejected") return "rejected";
      if (row.status === "downloaded") return "downloaded";
      if (row.isPaid == 1) return "paid";
      return row.status;
    },
    getStatusTitle(status) {
      const meta = this.getStatusMeta(status);
      if (meta.labels) {
        const locale = (this.$i18n && this.$i18n.locale) || 'en';
        return meta.labels[locale] || meta.labels['en'] || status;
      }
      if (meta.i18nKey && this.$t) return this.$t(meta.i18nKey);
      return this.getCaseStatusLabel(status);
    },
    getCaseStatusLabel(status) {
      // const map = {
      //   queued: this.$t("queued"),
      //   "in-progress": this.$t("inProgress"),
      //   failed: this.$t("failed"),
      //   unsupported: this.$t("unSupported"),
      //   "in-approval": this.$t("inApproval"),
      //   done: this.$t("approved"),
      //   paid: this.$t("paid"),
      //   downloaded: this.$t("downloaded") || "Downloaded",
      //   archived: this.$t("archived") || "Archived",
      //   hidden: this.$t("hidden"),
      // };
      const map = {
        created: this.$t("created"),
        processing: this.$t("processing"),
        reviewing: this.$t("reviewing"),
        adjusting: this.$t("adjusting"),
        done: this.$t("done"),
        paid: this.$t("paid"),
        downloaded: this.$t("downloaded"),
      };
      return map[status] || status;
    },
    async setFilterRatingValue(event) {
      this.filterRatingValue = event;
      await this.loadItems();
    },
    async setRatingValue(event, id, jawType) {
      await this.$store.dispatch("caseLists/saveRating", {
        caseId: id,
        ...(jawType === "upper"
          ? { upperCaseRating: event }
          : { lowerCaseRating: event }),
      });
    },
    handlePreviewClick(files) {
      this.currentUpper = files.upperOriginal;
      this.resultUpper = files.upperResult;
      this.currentLower = files.lowerOriginal;
      this.resultLower = files.lowerResult;
      this.showPreviewModal = true;
    },
    handlePreviewResultClick(array) {
      this.results = array;
      this.showPreviewResultModal = true;
    },
    onPageChange(params) {
      this.page = params.currentPage;
      this.loadItems();
    },
    onSortChange(params) {
      this.sortOrder = params[0].type;
      if (params[0].type == "none") this.sortOrder = "asc";
      this.sortBy = params[0].field;
      this.loadItems();
    },
    // async loadFlags() {
    //   let response = await this.$store.dispatch("flags/list");
    //   this.existingFlags = response?.data?.data;
    // },
    async loadCustomers() {
      await this.$store.dispatch("customers/list", {
        page: 1,
        perPage: 25,
        isMultiSelect: true,
      });
    },
    async loadItems(showLoading = true) {
      if (showLoading && !this.isAutoRefresh) {
        this.$store.commit("showLoader", true);
      }
      await this.$store
        .dispatch("caseLists/list", {
          page: this.page,
          ...this.form,
          ...(this.companyId ? { companyId: this.companyId, isCompany: true } : {}),
          sortBy: this.sortBy,
          sortOrder: this.sortOrder,
          selectedFilter: this.form.selectedFilter,
          starRating: this.filterRatingValue,
          createdFrom: this.form.createdFrom || "0001-01-01",
          createdTo: this.form.createdTo || "9999-12-31",
          productTypes: this.form.productTypes,
          needsManualApprovalFilter: this.form.needsManualApprovalFilter || "all",
          marginLineFilter: this.form.marginLineFilter || "all",
          caseStatus: (this.form.caseStatus || []).map((o) => (typeof o === "string" ? o : o.value)),
        })
        .then(async (res) => {
          this.rows = res?.data?.data;
          this.totalRecords = res?.data?.meta?.total;
          const userIds = (this.rows || [])
            .map((row) => this.getCaseUserId(row))
            .filter((userId) => userId);
          if (userIds.length) {
            await this.getUserProfiles(userIds);
          }

        await this.getUserProfiles(this.rows.map((row) => row.assignee));
        })
        .finally(() => {
          this.$store.commit("showLoader", false);
        });
    },
    resetRatingFilter() {
      this.filterRatingValue = null;
      this.form = {
        search: "",
        status: "all",
        selectedFilter: "all",
        perPage: 25,
        caseStatus: [],
        createdFrom: null,
        createdTo: null,
        companyIds: [],
        productTypes: [],
        needsManualApprovalFilter: "all",
        marginLineFilter: "all",
      };
      this.createdDateDraft = {
        from: null,
        to: null,
        selectedFilter: "all",
        companyIds: [],
        productTypes: [],
        needsManualApprovalFilter: "all",
        marginLineFilter: "all",
      };
    },
    getCaseUserId(row) {
      if (!row) return null;
      return row.lastEditedBy || null;
    },
    getUserFullName(userId) {
      if (!userId || !this.userProfiles) {
        return "";
      }
      const profile = this.userProfiles[userId];
      if (!profile) {
        return "";
      }
      const parts = [profile.first_name, profile.last_name].filter(
        (part) => part && part.trim()
      );
      return parts.join(" ").trim();
    },
    getUpdatedHoverText(row) {
      const dateText = this.$dateFormatter(row.updatedAt, this.$i18n.locale);
      const userId = this.getCaseUserId(row);
      const fullName = this.getUserFullName(userId);
      return fullName ? `${dateText} • ${this.$t("lastEditedBy")}: ${fullName}` : dateText;
    },
    openAssigneeModal(row) {
      this.selectedAssigneeId = row?.id || null;
      this.assignedAssignee = row?.assignee || null;
      this.showAssigneeModal = true;
    },
    closeAssigneeModal() {
      this.showAssigneeModal = false;
    },
    closePreviewModal() {
      this.showPreviewModal = false;
    },
    closePreviewResultModal() {
      this.showPreviewResultModal = false;
    },
  },
  async mounted() {
    await this.loadItems();
    // this.loadFlags();
    this.loadCustomers();
    if (!this.companyId) {
      this.intervalId = setInterval(() => {
        this.isAutoRefresh = true;
        this.loadItems(false);
        this.isAutoRefresh = false;
      }, 15000);
    }
  },
  beforeDestroy() {
    clearInterval(this.intervalId);
  },
  watch: {
    form: {
      handler() {
        this.debouncedFetch();
      },
      deep: true,
    },
  },
  // beforeRouteEnter(to, from, next) {
  //   document.body.classList.add("cookie-layout");
  //   next();
  // },
  // beforeRouteLeave(to, from, next) {
  //   document.body.classList.remove("cookie-layout");
  //   next();
  // },
};
</script>
<style lang="scss">
.form-dropdown {
  width: 100%;
  .dropdown-toggle {
    padding: 0 !important;
    height: auto !important;
    color: #fff;
    text-align: left;
    display: flex;
    align-items: center;
    justify-content: space-between;
    &::after {
      display: none;
    }
  }
}
.hour-glass {
  img {
    width: 50px;
  }
}

.failed {
  border: 1px solid #f00;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  svg {
    width: 24px;
    height: 24px;
    path {
      stroke: #f00;
    }
  }
}
</style>
<style scoped>
.status-chip {
  --chip-color: #6b7280;
  --chip-bg: rgba(107, 114, 128, 0.12);
  display: inline-flex;
  text-transform: uppercase;
  align-items: center;
  border-radius: 9999px;
  padding: 0.3rem 0.75rem;
  font-weight: 600;
  font-size: 0.91rem;
  color: var(--chip-color);
  background: var(--chip-bg);
  transition: background-color 0.15s ease, transform 0.15s ease;
  position: relative;
  overflow: hidden;
  white-space: nowrap;
}
.status-chip:hover {
  background-color: rgba(0, 0, 0, 0.06);
  transform: scale(1.03);
}
.status-chip[data-status="queued"],
.status-chip[data-status="in-queue"] {
  --chip-color: #b45309;
  --chip-bg: rgba(250, 204, 21, 0.25);
}
.status-chip[data-status="in-progress"],
.status-chip[data-status="processing"] {
  --chip-color: #1d4ed8;
  --chip-bg: rgba(59, 130, 246, 0.2);
}
.status-chip[data-status="failed"] {
  --chip-color: #b91c1c;
  --chip-bg: rgba(239, 68, 68, 0.18);
}
.status-chip[data-status="unsupported"],
.status-chip[data-status="not-supported"] {
  --chip-color: #c2410c;
  --chip-bg: rgba(251, 146, 60, 0.25);
}
.status-chip[data-status="review-needed"],
.status-chip[data-status="in-approval"] {
  --chip-color: #0891b2;
  --chip-bg: rgba(6, 234, 233, 0.32);
}
.status-chip[data-status="in-review"] {
  --chip-color: #1e3a8a;
  --chip-bg: rgba(37, 99, 235, 0.2);
}
.status-chip[data-status="approved"],
.status-chip[data-status="done"] {
  --chip-color: rgb(76, 94, 50);
  --chip-bg: rgb(211, 241, 167);
}
.status-chip[data-status="created"] {
  --chip-color: rgb(61, 90, 134);
  --chip-bg: rgb(207, 225, 253);
}
.status-chip[data-status="processing"] {
  --chip-color: rgb(134, 103, 152);
  --chip-bg: rgb(238, 215, 252);
}
.status-chip[data-status="reviewing"] {
  --chip-color: rgb(105, 53, 53);
  --chip-bg: rgb(252, 228, 166);
}
.status-chip[data-status="adjusting"] {
  --chip-color: rgb(80, 37, 63);
  --chip-bg: rgb(253, 208, 236);
}
.status-chip[data-status="rejected"] {
  --chip-color: rgb(124, 73, 22);
  --chip-bg: rgb(252, 228, 166);
}
.status-chip[data-status="paid"] {
  --chip-color: #374151;
  --chip-bg: rgba(107, 114, 128, 0.18);
}
.status-chip[data-status="downloaded"] {
  --chip-color: #374151;
  --chip-bg: rgba(107, 114, 128, 0.18);
}
.status-chip[data-status="archived"] {
  --chip-color: #4b5563;
  --chip-bg: rgba(209, 213, 219, 0.4);
}
.status-chip[data-status="hidden"] {
  --chip-color: #1f2937;
  --chip-bg: rgba(55, 65, 81, 0.25);
}
.created-chip {
  font-weight: 600;
  font-size: 0.85rem;
  color: #111827;
}
.created-filter-toggle {
  min-width: 170px;
  justify-content: space-between;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}
.ellipsis-text {
  max-width: 220px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin: 0;
}
.product-tag {
  height: 24px;
  padding: 2px 6px;
  font-size: 12px;
  gap: 6px;
  display: inline-flex;
  align-items: center;
  border-radius: 4px;
  font-weight: 600;
  white-space: nowrap;
}
.blue-tag {
  background: rgba(59, 130, 246, 0.16);
  color: #1d4ed8;
}
.gray-tag {
  background: rgba(107, 114, 128, 0.18);
  color: #374151;
}
.pink-tag {
  background: rgba(236, 72, 153, 0.14);
  color: #9d174d;
}
.dark-pink-tag {
  background: rgba(219, 39, 119, 0.18);
  color: #831843;
}
.undefined-tag {
    background: #0A143C;
    color: #ffffff;
  }
@keyframes sheenMove {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}
/* .status-chip[data-status="review-needed"]::after,
.status-chip[data-status="in-approval"]::after {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(
    120deg,
    transparent 20%,
    rgba(255, 255, 255, 0.35) 50%,
    transparent 80%
  );
  background-size: 200% 100%;
  mix-blend-mode: screen;
  animation: sheenMove 2.8s linear infinite;
} */
@media (prefers-reduced-motion: reduce) {
  .status-chip[data-status="review-needed"]::after,
  .status-chip[data-status="in-approval"]::after {
    animation: none;
  }
}
</style>
